import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { PoAccordionItemComponent, PoMultiselectOption, PoBreadcrumb, PoPageAction } from '@po-ui/ng-components';
import { Location } from '@angular/common';

import { MainService } from 'src/app/app.service';
import { NotificationServiceHM } from 'src/app/menu/notification-item/notification-item.service';
import { getApiErrorMessage, getHttpStatus } from '../core/utils/http-error-message.util';
import {
  Period,
  defaultPeriod,
  previousPeriod,
  rehydratePeriod
} from '../core/utils/period.util';
import {
  SOURCE_PROVIDER_OPTIONS,
  sourceProviderLabel
} from '../core/utils/provider-label.util';
import { resourceTypeLabel } from '../core/utils/resource-type-label.util';
import {
  UI_PREF_KEYS, readPref, sanitizeStringArray, writePref
} from '../core/utils/ui-prefs.util';
import { SmartReviewPermissionsService } from '../core/services/smart-review-permissions.service';
import { CostsAreasService } from '../admin/costs/costs-areas.service';
import { RESOURCE_TYPE_FILTER_OPTIONS } from '../admin/costs/utils/costs-resource.util';
import {
  ReviewStats,
  ReviewStatsBucket,
  ReviewStatsDimension,
  ReviewStatsFilters,
  ReviewStatsPeriodFilters
} from './interfaces/review-stats.interface';
import { ReviewStatsService } from './review-stats.service';
import { formatCount, formatSharePercent } from './utils/review-stats-format.util';
import { CurationStats } from '../admin/curation/interfaces/curation.interface';

/**
 * Para quem a tela está respondendo.
 *
 * - `self` — "o que eu preciso corrigir?": os achados, os arquivos e o feedback no
 *   recorte de quem abriu — o mesmo da lista de reviews: os próprios pedidos e as issues e
 *   PRs da área, ou tudo para quem tem visão global (admin, audit, superuser; decisão 0030).
 *   Sem os recortes por área e por usuário, que medem adoção e são da visão de serviço.
 * - `service` — "a ferramenta está entregando e calibrada?": o agregado, os recortes por
 *   pessoa e área, e a saúde do serviço.
 *
 * As duas audiências são o MESMO componente, e não duas telas: a diferença é qual
 * pergunta cada uma responde, e duas cópias divergiriam no primeiro card novo.
 */
export type ReviewDashboardAudience = 'self' | 'service';

/** O recorte guardado no navegador entre visitas — mesma decisão de `CostsPrefs`: área
 * e usuário ficam de fora porque as opções deles dependem da resposta (ver lá). */
interface ReviewStatsPrefs {
  period: Period | null;
  resourceTypes: string[];
  sourceProviders: string[];
}

const NO_BUCKETS: readonly ReviewStatsBucket[] = Object.freeze([]);

/**
 * Dashboard de reviews — orquestrador. Monta o recorte (período + filtros), chama
 * `GET /reviews/stats` uma vez com todas as dimensões (resourceType, sourceProvider,
 * user, area) pedidas em `groupBy`, e distribui a resposta entre os KPIs, o gráfico de
 * série e as quatro seções de breakdown.
 *
 * O bloco `health` só chega quando o backend reconhece `smartreview.admin` no token —
 * o template lê `stats?.health` direto (`*ngIf`), sem replicar a checagem de
 * capability aqui: a ausência do campo na resposta já É a autoridade, e uma segunda
 * checagem no front só criaria um lugar em que as duas podem discordar.
 */
/** Um bloco de "Operação agora" — ver `healthTiles`. */
interface HealthTile {
  label: string;
  value: string;
  hint: string;
  /** Tipo do `po-tag`; vale só quando `tag` não é vazio. */
  state: 'info' | 'warning' | 'danger';
  tag: string;
  help: string;
}

@Component({
  selector: 'app-review-dashboard',
  templateUrl: './review-dashboard.component.html',
  styleUrls: ['./review-dashboard.component.css']
})
export class ReviewDashboardComponent implements OnInit, OnDestroy {
  loading = false;
  stats: ReviewStats | null = null;

  /**
   * A mesma consulta sobre a janela imediatamente anterior, de igual duração — é o que
   * transforma "340 reviews" em "340 reviews, 12% acima do mês passado". Fica `null`
   * quando não há período fechado ("Todo o período") ou quando a segunda chamada
   * falhou; os cards então simplesmente não mostram a variação.
   */
  previousStats: ReviewStats | null = null;
  hotspotFilters: ReviewStatsPeriodFilters = {};

  period: Period = defaultPeriod();

  selectedResourceTypes: string[] = [];
  selectedSourceProviders: string[] = [];
  selectedAreas: string[] = [];
  selectedUsers: string[] = [];

  readonly resourceTypeOptions: PoMultiselectOption[] = RESOURCE_TYPE_FILTER_OPTIONS;
  readonly sourceProviderOptions: PoMultiselectOption[] = SOURCE_PROVIDER_OPTIONS;

  areaOptions: PoMultiselectOption[] = [];
  userOptions: PoMultiselectOption[] = [];

  readonly healthCardHelp = {
    waiting:
      'Jobs que ainda não começaram a execução. O total soma os pedidos aguardando publicação ' +
      'com os que já estão na fila esperando um worker disponível.',
    processing: 'Jobs que já foram retirados da fila e estão sendo executados por um worker.',
    retries:
      'Jobs que falharam na última hora e aguardam a próxima tentativa (1 min, 5 min, 30 min). ' +
      'Recuperam-se sozinhos se a causa for passageira.',
    stalled:
      'Jobs em falha há mais de uma hora, fora da cadeia de retentativa. Ou o worker está ' +
      'pausado ou fora do ar — e voltam sozinhos quando ele voltar —, ou a retentativa se ' +
      'perdeu e precisam ser reprocessados. Confira os controles antes de reprocessar.',
    dead:
      'Jobs dos últimos 7 dias que esgotaram as tentativas e foram para a DLQ. Não voltam ' +
      'sozinhos; somem da conta quando o review é reprocessado com sucesso. Veja a causa ' +
      'abaixo e reprocesse depois de corrigir.'
  };

  /**
   * Os votos do time, lidos pelo card de calibragem do Painel de Curadoria e repassados ao
   * indicador de falso positivo do topo — uma leitura só, sem segunda fonte.
   */
  calibration: CurationStats | null = null;

  /** Abre as causas das falhas uma vez por carga quando há algo na DLQ. */
  private causesAutoOpened = false;

  /**
   * As causas das falhas começam recolhidas: com a fila em dia, a lista inteira empurrava
   * o resto da tela para baixo. Com algo na DLQ ou parado — que a retentativa não
   * resolve — ela abre.
   */
  @ViewChild('failureCauses') set failureCausesItem(item: PoAccordionItemComponent | undefined) {
    if (!item || this.causesAutoOpened || !this.healthNeedsAttention) { return; }
    this.causesAutoOpened = true;
    setTimeout(() => item.expand());
  }

  /**
   * Para quem esta instância está respondendo. Resolvida no `ngOnInit` a partir da rota
   * e da capability — ver `resolveAudience`.
   */
  audience: ReviewDashboardAudience = 'self';

  /**
   * Os recortes só existem na visão de serviço, onde medem adoção. Para o dev,
   * "composição por tipo" é o que ele mesmo escolheu pedir e "origem" é quase sempre
   * 100% TFVC (10 de 706 pedidos eram git em produção): nenhum dos dois suporta uma
   * decisão dele, e pedir as agregações só para escondê-las seria pagar à toa.
   */
  private get dimensions(): ReviewStatsDimension[] {
    return this.audience === 'service'
      ? ['resourceType', 'sourceProvider', 'language', 'user', 'area']
      : [];
  }

  private readonly destroy$ = new Subject<void>();
  private readonly loadRequests = new Subject<void>();
  private loadTimer: number | null = null;

  /**
   * Título, trilha e "Voltar" só existem na visão do dev, que é uma página própria. Na
   * de administração o componente é filho do `AdminShellComponent`, que já abre um
   * `po-page-default` com título e abas: repetir os três ali daria dois cabeçalhos e um
   * "Voltar" que sai do shell inteiro. É o mesmo desenho do painel de custos, que é
   * filho do mesmo shell e não traz nenhum dos três.
   */
  get pageTitle(): string | undefined {
    return this.isServiceView ? undefined : 'Dashboard de reviews';
  }

  get breadcrumb(): PoBreadcrumb | undefined {
    if (this.isServiceView) { return undefined; }
    return {
      items: [
        { label: 'Reviews', link: '/menu/smart-review/reviews' },
        { label: 'Dashboard de reviews' }
      ]
    };
  }

  get pageActions(): PoPageAction[] {
    return this.isServiceView ? this.serviceActions : this.dashboardActions;
  }

  /**
   * Array fixo, e vazio congelado no outro caminho: um getter que devolvesse literal
   * novo a cada ciclo faria o `po-page-default` ver "as ações mudaram" sempre — mesma
   * armadilha de `[p-columns]` documentada em `costs-panel.component.ts`.
   */
  /**
   * Na visão de serviço, só "Atualizar": o mesmo botão no topo à direita que Controles,
   * Curadoria e Custos mostram — cada aba da Administração é um po-page-default com as
   * próprias ações. "Voltar" não cabe: quem navega ali são as abas.
   */
  private readonly serviceActions: PoPageAction[] = [
    { label: 'Atualizar', icon: 'po-icon-refresh', action: () => this.refresh() }
  ];

  private readonly dashboardActions: PoPageAction[] = [
    {
      label: 'Atualizar',
      icon: 'po-icon-refresh',
      action: () => this.refresh()
    },
    { label: 'Voltar', icon: 'po-icon-arrow-left', action: () => this.location.back() }
  ];

  constructor(
    private statsService: ReviewStatsService,
    private areasService: CostsAreasService,
    private permissions: SmartReviewPermissionsService,
    private route: ActivatedRoute,
    private mainService: MainService,
    private notification: NotificationServiceHM,
    private router: Router,
    private location: Location
  ) {
    this.watchLoadRequests();
  }

  ngOnInit(): void {
    this.mainService.validSession();
    this.audience = this.resolveAudience();
    this.restorePrefs();
    // O catálogo de áreas só alimenta filtro e rótulo da visão de serviço; na do dev
    // não há o que traduzir, e a requisição ao ESP seria paga à toa.
    if (this.audience === 'service') { this.loadAreaNames(); }
    this.load();
  }

  /**
   * A rota diz qual audiência ela serve (`data.audience`); a capability decide se quem
   * abriu pode recebê-la. A rota sozinha não basta — quem digita a URL de administração
   * não pode ganhar a visão do serviço.
   *
   * A audiência decide QUAIS blocos aparecem, não QUAIS reviews entram: o recorte é do
   * backend, igual ao da lista de reviews (decisão 0030). Quem tem visão global vê tudo
   * nas duas audiências; a checagem aqui é de UI, não de autorização.
   */
  private resolveAudience(): ReviewDashboardAudience {
    const wanted = this.route.snapshot.data?.['audience'];
    // Admin, audit e superuser leem todos os reviews — a mesma regra de leitor global
    // que o backend aplica ao recorte —, então os três podem abrir a visão de serviço.
    const allowed = this.permissions.hasAdmin() || this.permissions.hasAudit();
    return wanted === 'service' && allowed ? 'service' : 'self';
  }

  /**
   * Visão pessoal de quem tem visão global: os números são de todas as áreas e pessoas, e
   * a tela diz isso — sem o aviso, "Dashboard de reviews" parece falar só de quem abriu.
   */
  get showsEveryonesReviews(): boolean {
    return !this.isServiceView && (this.permissions.hasAdmin() || this.permissions.hasAudit());
  }

  /** Visão do dev: os recortes por pessoa e por área não respondem nada a ela. */
  get isServiceView(): boolean {
    return this.audience === 'service';
  }

  ngOnDestroy(): void {
    if (this.loadTimer != null) {
      window.clearTimeout(this.loadTimer);
      this.loadTimer = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private restorePrefs(): void {
    const saved = readPref(UI_PREF_KEYS.reviewStatsFilters, (raw): ReviewStatsPrefs | null => {
      if (!raw || typeof raw !== 'object') { return null; }
      const value = raw as Record<string, unknown>;
      return {
        period: rehydratePeriod(value['period']),
        resourceTypes: sanitizeStringArray(
          value['resourceTypes'],
          this.resourceTypeOptions.map(option => String(option.value))
        ),
        sourceProviders: sanitizeStringArray(
          value['sourceProviders'],
          this.sourceProviderOptions.map(option => String(option.value))
        )
      };
    });

    if (!saved) { return; }

    if (saved.period) { this.period = saved.period; }
    this.selectedResourceTypes = saved.resourceTypes;
    this.selectedSourceProviders = saved.sourceProviders;
  }

  private savePrefs(): void {
    writePref(UI_PREF_KEYS.reviewStatsFilters, {
      period: this.period,
      resourceTypes: this.selectedResourceTypes,
      sourceProviders: this.selectedSourceProviders
    });
  }

  private get filters(): ReviewStatsFilters {
    const filters: ReviewStatsFilters = { groupBy: this.dimensions };
    if (this.period.dateFrom) { filters.dateFrom = this.period.dateFrom; }
    if (this.period.dateTo) { filters.dateTo = this.period.dateTo; }
    if (this.selectedResourceTypes.length) { filters.resourceType = [...this.selectedResourceTypes]; }
    if (this.selectedSourceProviders.length) { filters.sourceProvider = [...this.selectedSourceProviders]; }
    if (this.selectedAreas.length) { filters.area = [...this.selectedAreas]; }
    if (this.selectedUsers.length) { filters.user = [...this.selectedUsers]; }
    return filters;
  }

  /** Agrupa chamadas a `load()` vindas do mesmo clique — mesma defesa de `CostsPanelComponent`. */
  private scheduleLoad(): void {
    if (this.loadTimer != null) { return; }
    this.loadTimer = window.setTimeout(() => {
      this.loadTimer = null;
      this.load();
    });
  }

  private watchLoadRequests(): void {
    this.loadRequests
      .pipe(
        switchMap(() => forkJoin({
          stats: this.statsService.getStats(this.filters),
          previous: this.loadPrevious()
        }).pipe(
          map(result => ({ ...result, error: null as unknown })),
          catchError(error => of({
            stats: null as ReviewStats | null,
            previous: null as ReviewStats | null,
            error: error as unknown
          }))
        )),
        takeUntil(this.destroy$)
      )
      .subscribe(({ stats, previous, error }) => {
        this.loading = false;
        if (error) {
          this.stats = null;
          this.previousStats = null;
          this.handleError(error);
          return;
        }
        this.stats = stats;
        this.previousStats = previous;
        this.refreshUserOptions();
      });
  }

  /**
   * A janela imediatamente anterior, de mesma duração — a base das variações dos KPIs.
   * Mesmo desenho de `CostsPanelComponent.loadPrevious`:
   *
   * - `groupBy` vazio: a comparação só lê totais e achados, e pedir os sete recortes
   *   pagaria sete agregações que ninguém exibe.
   * - erro aqui NÃO derruba a tela (`catchError` → `of(null)`): a comparação é
   *   acessório, e uma falha nela não pode esconder os números do período atual.
   * - sem período fechado ("Todo o período") não há janela anterior a espelhar, e
   *   `previousPeriod` devolve `null` — comparar contra um intervalo arbitrário seria
   *   pior que não comparar.
   */
  private loadPrevious(): Observable<ReviewStats | null> {
    const previous = previousPeriod(this.period);
    if (!previous) { return of(null); }

    return this.statsService
      .getStats({ ...this.filters, ...previous, groupBy: [] })
      .pipe(catchError(() => of(null)));
  }

  load(): void {
    this.causesAutoOpened = false;
    this.savePrefs();
    const { groupBy: _groupBy, ...hotspotFilters } = this.filters;
    this.hotspotFilters = hotspotFilters;
    this.loading = true;
    this.loadRequests.next();
  }

  /** Atualiza o recorte atual sem permitir requisições concorrentes pelo botão. */
  refresh(): void {
    if (this.loading) { return; }
    this.load();
  }

  onPeriodChange(period: Period): void {
    this.period = period;
    this.load();
  }

  onResourceTypeChange(types: string[]): void {
    this.selectedResourceTypes = types ?? [];
    this.scheduleLoad();
  }

  onSourceProviderChange(providers: string[]): void {
    this.selectedSourceProviders = providers ?? [];
    this.scheduleLoad();
  }

  onAreaChange(areas: string[]): void {
    this.selectedAreas = areas ?? [];
    this.scheduleLoad();
  }

  onUserChange(users: string[]): void {
    this.selectedUsers = users ?? [];
    this.scheduleLoad();
  }

  /**
   * Recorte sem nenhum review. Vale um aviso único, e não os sete widgets de recorte
   * cada um repetindo "Sem dados neste período" — sete respostas vazias para a mesma
   * pergunta escondem a única informação que existe, que é "não houve review aqui".
   * O card de saúde continua fora deste corte: ele descreve o serviço agora, não o
   * recorte, e é justamente quando não há review que ele importa.
   */
  /** O bloco de saúde só na visão de serviço: descreve o serviço, não quem pergunta. */
  get serviceHealth(): ReviewStats['health'] {
    return this.isServiceView ? this.stats?.health : undefined;
  }

  get isEmptyPeriod(): boolean {
    return !!this.stats && this.stats.totals.total === 0;
  }

  /**
   * Severidade e veredito dependem do detalhamento materializado a partir dos artefatos.
   * Dumps apenas do PostgreSQL podem ter os totais de achados sem esses campos; nesse
   * caso, esconder o bloco evita transformar dado ausente em dois KPIs sem resposta.
   */
  get hasDetailedResults(): boolean {
    const findings = this.stats?.findings;
    const quality = this.stats?.quality;
    return (findings?.detailedReviews ?? 0) > 0 || (quality?.files ?? 0) > 0;
  }

  private bucketsOf(dimension: ReviewStatsDimension): readonly ReviewStatsBucket[] {
    return this.stats?.breakdown?.[dimension] ?? NO_BUCKETS;
  }

  get resourceTypeRows(): readonly ReviewStatsBucket[] { return this.bucketsOf('resourceType'); }
  get sourceProviderRows(): readonly ReviewStatsBucket[] { return this.bucketsOf('sourceProvider'); }
  get userRows(): readonly ReviewStatsBucket[] { return this.bucketsOf('user'); }
  get areaRows(): readonly ReviewStatsBucket[] { return this.bucketsOf('area'); }
  get languageRows(): readonly ReviewStatsBucket[] { return this.bucketsOf('language'); }

  // ─── Saúde do serviço (bloco `health`, só para quem tem `smartreview.admin`) ────

  /**
   * Um widget por estado do job. A tag só aparece com algo no estado e diz o que se pede
   * de quem olha: acompanhar (fila, execução), esperar (retentativa volta sozinha) ou agir
   * (DLQ). Zerado, o número fica apagado e sem tag.
   */
  get healthTiles(): HealthTile[] {
    const h = this.stats?.health;
    if (!h) { return []; }
    const waiting = h.queued + h.outboxPending;
    return [
      { label: 'Na fila', value: formatCount(waiting), hint: 'aguardando um worker',
        state: 'info', tag: waiting > 0 ? 'Aguardando' : '', help: this.healthCardHelp.waiting },
      { label: 'Processando', value: formatCount(h.processing), hint: 'em execução agora',
        state: 'info', tag: h.processing > 0 ? 'Em execução' : '', help: this.healthCardHelp.processing },
      { label: 'Em retentativa', value: formatCount(h.retries), hint: 'voltam sozinhas à fila',
        state: 'warning', tag: h.retries > 0 ? 'Recupera sozinho' : '', help: this.healthCardHelp.retries },
      { label: 'Parados', value: formatCount(this.healthStalled), hint: 'sem retentativa há mais de 1h',
        state: 'warning', tag: this.healthStalled > 0 ? 'Verificar' : '', help: this.healthCardHelp.stalled },
      { label: 'DLQ', value: formatCount(h.dead), hint: 'últimos 7 dias, sem as já resolvidas',
        state: 'danger', tag: h.dead > 0 ? 'Exige ação' : '', help: this.healthCardHelp.dead }
    ];
  }

  /** Nada na fila, processando ou falhando: diz "em dia" ao lado dos quatro zeros. */
  /** Os getters de lista devolvem objetos novos a cada ciclo: sem isto o ngFor recriaria os widgets. */
  trackByLabel(_index: number, item: { label: string }): string {
    return item.label;
  }

  /** Cinco filtros na visão de serviço, três na do dev: as duas linhas fecham em 12. */
  get filterClass(): string {
    return this.isServiceView ? 'po-sm-12 po-md-4 po-lg-2' : 'po-sm-12 po-md-4 po-lg-4';
  }

  /** O card ao lado de Risco e Resultado: Calibragem no serviço, o feedback do dev. */
  get hasSideQualityCard(): boolean {
    return this.isServiceView || !!this.stats?.feedback;
  }

  get periodSectionHint(): string {
    return this.isServiceView
      ? 'Volume, qualidade e adoção no recorte dos filtros.'
      : 'Volume e qualidade no recorte dos filtros.';
  }

  get healthAllClear(): boolean {
    const h = this.stats?.health;
    return !!h && h.queued + h.outboxPending + h.processing + h.retries + h.dead + this.healthStalled === 0;
  }

  /** Ausente em backend anterior à decisão 0032 — conta como zero. */
  get healthStalled(): number {
    return this.stats?.health?.stalled ?? 0;
  }

  /** O que não se resolve pela retentativa: DLQ ou parados. Abre as causas e dá o tom da tag. */
  get healthNeedsAttention(): boolean {
    return this.hasHealthDead || this.healthStalled > 0;
  }

  get failureCausesTag(): { label: string; type: 'danger' | 'warning' } {
    if (this.hasHealthDead) { return { label: 'Exige ação', type: 'danger' }; }
    if (this.healthStalled > 0) { return { label: 'Verificar', type: 'warning' }; }
    return { label: 'Recupera sozinho', type: 'warning' };
  }

  get hasHealthDead(): boolean {
    return (this.stats?.health?.dead ?? 0) > 0;
  }

  get failureCausesLabel(): string {
    const n = this.stats?.health?.topErrors?.length ?? 0;
    return n === 1 ? '1 causa de falha' : `${n} causas de falha`;
  }

  /** O falso positivo do topo, dos mesmos votos do card de calibragem. */
  get falsePositive(): { rate: string; votes: string } | null {
    const votes = this.calibration?.votes;
    if (!votes?.total) { return null; }
    return {
      rate: formatSharePercent(votes.falsePositive, votes.total),
      votes: `${formatCount(votes.falsePositive)} de ${formatCount(votes.total)} votos`
    };
  }

  onCalibrationLoaded(stats: CurationStats | null): void {
    this.calibration = stats;
  }

  keysOf(dimension: ReviewStatsDimension): number | undefined {
    return this.stats?.breakdownKeys?.[dimension];
  }

  /**
   * Rótulo de exibição da chave de área — mesma tradução id → descrição do Painel de Custos.
   * Trocado por uma função NOVA quando o catálogo chega: se as estatísticas chegam antes,
   * o breakdown já montou as barras com o id cru, e só uma referência nova no input faz
   * ele rodar `ngOnChanges` de novo e trocar o id pelo nome.
   */
  areaLabel = this.areaLabelFor(new Map());

  resourceTypeLabelFn = (key: string): string => resourceTypeLabel(key) || key;

  sourceProviderLabelFn = (key: string): string => sourceProviderLabel(key) || key;

  private areaLabelFor(names: Map<string, string>): (key: string) => string {
    return key => CostsAreasService.label(key, names);
  }

  /**
   * Catálogo de áreas do ESP: alimenta o filtro e traduz as chaves do breakdown.
   * Carrega em paralelo com `load()` — a tela não espera por ele.
   */
  private loadAreaNames(): void {
    this.areasService.areaNames().pipe(takeUntil(this.destroy$)).subscribe(names => {
      this.areaLabel = this.areaLabelFor(names);
      this.areaOptions = CostsAreasService.toOptions(names);
    });
  }

  /**
   * Opções do filtro de usuário, derivadas do próprio breakdown — não há endpoint de
   * usuários do Smart Review. Só recalculada sem seleção ativa, mesmo motivo de
   * `CostsPanelComponent.refreshUserOptions`.
   */
  private refreshUserOptions(): void {
    if (this.selectedUsers.length) { return; }
    this.userOptions = this.bucketsOf('user')
      .map(bucket => bucket.key)
      .filter(key => !key.startsWith('('))
      .map(key => ({ value: key, label: key }));
  }

  private handleError(error: unknown): void {
    if (getHttpStatus(error) === 403) {
      this.notification.showToast('Acesso ao dashboard de reviews do Smart Review negado.', 'warning');
      this.router.navigate(['/menu/smart-review/reviews']);
      return;
    }
    this.notification.showToast(getApiErrorMessage(error, 'Erro ao carregar o dashboard de reviews.'), 'error');
  }
}
